import type { LocalProfile } from "./auth-types";
import { resolvePostLoginRoute } from "./post-login-route";
import { isStudentSignupIntent, TEACHER_SELF_SERVICE_SIGNUP_ENABLED } from "./signup-intent";

export const POST_LOGIN_RETURN_TO_KEY = "ccs.auth.return_to";

/** Accept only same-origin relative app paths (blocks open redirects). */
export function sanitizeReturnTo(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const path = raw.trim();
  if (!path.startsWith("/")) return null;
  if (path.startsWith("//")) return null;
  if (path.startsWith("/login")) return null;
  if (path.startsWith("/auth/callback")) return null;
  if (path.startsWith("/onboarding")) return null;
  return path;
}

export function savePostLoginReturnTo(path: string | null | undefined): void {
  const safe = sanitizeReturnTo(path);
  if (!safe) return;
  try {
    sessionStorage.setItem(POST_LOGIN_RETURN_TO_KEY, safe);
  } catch {
    /* storage unavailable */
  }
}

export function loadPostLoginReturnTo(): string | null {
  try {
    return sanitizeReturnTo(sessionStorage.getItem(POST_LOGIN_RETURN_TO_KEY));
  } catch {
    return null;
  }
}

export function clearPostLoginReturnTo(): void {
  try {
    sessionStorage.removeItem(POST_LOGIN_RETURN_TO_KEY);
  } catch {
    /* ignore */
  }
}

export function buildLoginPath(returnTo?: string | null, intent?: "teacher" | "student" | null): string {
  const safe = sanitizeReturnTo(returnTo);
  const params = new URLSearchParams();
  if (safe) params.set("returnTo", safe);
  if (intent === "student" || (intent === "teacher" && TEACHER_SELF_SERVICE_SIGNUP_ENABLED)) {
    params.set("intent", intent);
  }
  const qs = params.toString();
  return qs ? `/login?${qs}` : "/login";
}

export function isReturnToAllowedForProfile(path: string, profile: LocalProfile): boolean {
  if (profile.role === "pending") {
    return isStudentSignupIntent(profile, loadPostLoginReturnTo()) && path.startsWith("/join");
  }

  if (profile.role === "student" && profile.enrollmentSummary === "archived_only") {
    return path.startsWith("/no-active-classes") || path.startsWith("/join");
  }

  if (profile.role === "teacher") {
    return (
      path.startsWith("/teacher") ||
      path.startsWith("/lesson-preview") ||
      path.startsWith("/playground") ||
      path.startsWith("/admin")
    );
  }

  if (profile.role === "student") {
    return (
      path.startsWith("/lessons") ||
      path.startsWith("/cohorts") ||
      path.startsWith("/join") ||
      path.startsWith("/ide") ||
      path.startsWith("/playground") ||
      path.startsWith("/no-active-classes")
    );
  }

  return false;
}

/**
 * Where to send the user after sign-in (and onboarding) completes.
 * Honors a saved deep link when it matches their role.
 */
export function resolvePostAuthRoute(profile: LocalProfile): string {
  if (profile.role === "pending") {
    return "/onboarding";
  }

  const saved = loadPostLoginReturnTo();
  if (saved && isReturnToAllowedForProfile(saved, profile)) {
    clearPostLoginReturnTo();
    return saved;
  }

  clearPostLoginReturnTo();
  return resolvePostLoginRoute(profile);
}
